import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import Loader from 'rsuite/Loader';

import initializeDb from '../rxdb/database/initialize-db';

const messages = defineMessages({
  loading: {
    id: 'app.database.loading',
    defaultMessage: 'Connecting to database…'
  },
  failed: {
    id: 'app.database.failed',
    defaultMessage: 'Cannot connect to database'
  }
});

/**
 * Database context
 */
const DatabaseContext = React.createContext();

/**
 * Database hook
 *
 * @returns database
 */
export const useDatabase = () => React.useContext(DatabaseContext);

/**
 * Database Provider component
 *
 * Connects to the database and provides access to it
 * for all nested children via db context.
 */
export const DbProvider = ({ children }) => {
  const { formatMessage } = useIntl();

  const [database, setDatabase] = React.useState();
  const [error, setError] = React.useState();

  // database connection
  const initDB = async () => {
    setDatabase(null);
    setError(null);

    const { err, db } = await initializeDb();
    setDatabase(db);
    setError(err);
  };

  React.useEffect(() => {
    initDB();

    return () => {
      setDatabase();
      setError();
    };
  }, []);

  if (error) {
    return (
      <span data-testid='app-db-failed'>
        {formatMessage(messages.failed)}
      </span>
    );
  }

  if (database) {
    return (
      <DatabaseContext.Provider value={database}>
        {children}

        <span data-testid='app-db-loaded' />
      </DatabaseContext.Provider>
    );
  }

  return (
    <span data-testid='app-db-loading'>
      <Loader
        center
        vertical
        speed='slow'
        size='lg'
        content={formatMessage(messages.loading)}
      />
    </span>
  );
};
